import { toArrayBuffer } from "@/lib/utils/array-buffer";
import { useCallback, type ComponentProps, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle, Button, DataTable, Input, Select, Skeleton, type DataTableColumn } from "@akuna/ui";
import { PrescriptionViewDialog } from "@/components/prescriptions/PrescriptionViewDialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { buildPrescriptionsZip } from "./export-zip";
import { buildReportRpcParams } from "./rpc-params";
import { mapReportRows, type RawPrescriptionReportRow } from "./map-rows";
import { useMapaPrescriptionsReport, useMapaReportVeterinarians } from "@/hooks/useMapaPrescriptionsReport";
import { SalesExplorerPeriodBar } from "../sales-explorer/SalesExplorerPeriodBar";
import { resolvePeriodRange, type PeriodPreset } from "../sales-explorer/period-presets";
import { formatSequence } from "./map-rows";
import { PAGE_SIZE, type MapaMode, type PrescriptionReportRow, type PrescriptionsReportFilters } from "./types";

type PrescriptionForDialog = ComponentProps<typeof PrescriptionViewDialog>["prescription"];

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

const EXPORT_PAGE_SIZE = 100;
const ALL_VETS = "all";
const SEARCH_DEBOUNCE_MS = 300;

function useDebouncedValue<T>(value: T, delay = SEARCH_DEBOUNCE_MS): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

const dateOnly = (value: string | null, locale: string) =>
  value ? new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(new Date(value)) : "—";

export function PrescriptionsReportTab() {
  const { i18n, t } = useTranslation("reports");
  const locale = i18n.language;

  const [range, setRange] = useState(() => ({ period: "month" as PeriodPreset, ...resolvePeriodRange("month") }));
  const [searchInput, setSearchInput] = useState("");
  const [sequenceInput, setSequenceInput] = useState("");
  const [vetId, setVetId] = useState("");
  const [mapaMode, setMapaMode] = useState<MapaMode>("all");
  const [page, setPage] = useState(1);
  const [selectedPrescription, setSelectedPrescription] = useState<PrescriptionForDialog | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);

  const handleRowClick = useCallback(
    async (row: PrescriptionReportRow) => {
      const { data, error: fetchError } = await supabase
        .from("medical_prescriptions")
        .select(
          "*, items:medical_prescription_items(*), exam_items:medical_prescription_exam_items(*), vet:profiles!medical_prescriptions_vet_user_id_fkey(id, full_name, address), clinic:clinics(id, name)",
        )
        .eq("id", row.id)
        .single();
      if (fetchError || !data) {
        toast.error(t("mapaRxOpenError"));
        return;
      }
      setSelectedPrescription(data as unknown as PrescriptionForDialog);
      setIsViewOpen(true);
    },
    [t],
  );
  const { data: vets } = useMapaReportVeterinarians();

  const search = useDebouncedValue(searchInput);
  const sequence = useDebouncedValue(sequenceInput);

  // Qualquer mudança de filtro volta para a página 1.
  useEffect(() => {
    setPage(1);
  }, [search, sequence, vetId, mapaMode]);

  const filters = useMemo<PrescriptionsReportFilters>(
    () => ({ from: range.from, to: range.to, period: range.period, search, sequence, vetId, mapaMode, page }),
    [mapaMode, page, range, search, sequence, vetId],
  );

  const { data, error, isLoading } = useMapaPrescriptionsReport(filters);

  const [exportProgress, setExportProgress] = useState<{ done: number; total: number } | null>(null);

  const handleExport = useCallback(async () => {
    setExportProgress({ done: 0, total: 0 });
    try {
      // Mesmos filtros da tela, sem paginação.
      const rows: PrescriptionReportRow[] = [];
      for (let exportPage = 1; ; exportPage += 1) {
        const params = buildReportRpcParams({ ...filters, page: exportPage }, EXPORT_PAGE_SIZE);
        const { data: raw, error: rpcError } = await supabase.rpc("get_mapa_prescriptions_report" as never, params as never);
        if (rpcError) throw rpcError;
        const result = mapReportRows(raw as unknown as RawPrescriptionReportRow[] | null);
        rows.push(...result.rows);
        if (rows.length >= result.totalCount || result.rows.length === 0) break;
      }
      if (rows.length === 0) {
        toast.info(t("mapaRxExportEmpty"));
        return;
      }
      setExportProgress({ done: 0, total: rows.length });
      const { bytes, failed } = await buildPrescriptionsZip(rows, (done, total) =>
        setExportProgress({ done, total }),
      );
      downloadBlob(
        new Blob([toArrayBuffer(bytes)], { type: "application/zip" }),
        `receitas_${filters.from.slice(0, 10)}_${filters.to.slice(0, 10)}.zip`,
      );
      if (failed > 0) toast.warning(t("mapaRxExportPartial", { count: failed }));
    } catch (exportError) {
      console.error(exportError);
      toast.error(t("mapaRxExportError"));
    } finally {
      setExportProgress(null);
    }
  }, [filters, t]);

  const handlePeriodChange = useCallback((period: PeriodPreset) => {
    setRange({ period, ...resolvePeriodRange(period) });
    setPage(1);
  }, []);

  const handleCustomRangeChange = useCallback((from: Date, to: Date) => {
    setRange((current) => ({ ...current, from: from.toISOString(), to: to.toISOString() }));
    setPage(1);
  }, []);

  const columns: DataTableColumn<PrescriptionReportRow>[] = [
    {
      cell: (row) => (
        <span className="flex flex-col leading-tight">
          <span className="font-semibold tabular-nums text-foreground">{formatSequence(row.sequence)}</span>
          {row.identifier ? <span className="truncate text-xs text-muted-foreground">{row.identifier}</span> : null}
        </span>
      ),
      header: t("mapaRxColSequence"),
      id: "sequence",
      minWidth: 150,
      width: 190,
    },
    {
      cell: (row) => dateOnly(row.issuedAt, locale),
      header: t("mapaRxColDate"),
      id: "date",
      minWidth: 100,
      width: 120,
    },
    {
      cell: (row) =>
        row.petId && row.petName ? (
          <Link className="truncate font-semibold text-primary hover:underline" to={`/patients/pets/${row.petId}`}>
            {row.petName}
          </Link>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
      header: t("mapaRxColPet"),
      id: "pet",
      minWidth: 90,
      width: 120,
    },
    {
      cell: (row) =>
        row.ownerId && row.ownerName ? (
          <Link className="truncate text-primary hover:underline" to={`/patients/owners/${row.ownerId}`}>
            {row.ownerName}
          </Link>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
      grow: true,
      header: t("mapaRxColOwner"),
      id: "owner",
      minWidth: 120,
      width: 150,
    },
    {
      cell: (row) => row.vetName ?? <span className="text-muted-foreground">—</span>,
      header: t("mapaRxColVet"),
      id: "vet",
      minWidth: 220,
      width: 300,
    },
  ];

  return (
    <div className="space-y-5">
      <SalesExplorerPeriodBar
        description={t("mapaRxDesc")}
        from={range.from}
        onCustomRangeChange={handleCustomRangeChange}
        onPeriodChange={handlePeriodChange}
        period={range.period}
        title={t("mapaRxTabLabel")}
        to={range.to}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground" htmlFor="mapa-rx-search">
            {t("mapaRxSearchLabel")}
          </label>
          <Input
            id="mapa-rx-search"
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder={t("mapaRxSearchPlaceholder")}
            value={searchInput}
          />
        </div>
        <Select
          label={t("mapaRxVetLabel")}
          onValueChange={(value) => setVetId(value === ALL_VETS ? "" : value)}
          options={[
            { label: t("mapaRxVetAll"), value: ALL_VETS },
            ...(vets ?? []).map((vet) => ({ label: vet.full_name, value: vet.id })),
          ]}
          value={vetId || ALL_VETS}
        />
        <Select
          label={t("mapaRxModeLabel")}
          onValueChange={(value) => setMapaMode(value as MapaMode)}
          options={[
            { label: t("mapaRxModeAll"), value: "all" },
            { label: t("mapaRxModeWith"), value: "with" },
            { label: t("mapaRxModeWithout"), value: "without" },
          ]}
          value={mapaMode}
        />
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground" htmlFor="mapa-rx-sequence">
            {t("mapaRxSequenceLabel")}
          </label>
          <Input
            disabled={mapaMode !== "with"}
            id="mapa-rx-sequence"
            inputMode="numeric"
            onChange={(event) => setSequenceInput(event.target.value)}
            placeholder={t("mapaRxSequencePlaceholder")}
            value={sequenceInput}
          />
        </div>
      </div>

      {error ? (
        <Alert variant="danger">
          <AlertTitle>{t("mapaRxErrorTitle")}</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      ) : isLoading ? (
        <Skeleton label={t("mapaRxLoading")} />
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">{t("mapaRxCount", { count: data?.totalCount ?? 0 })}</p>
            <Button
              disabled={exportProgress !== null || (data?.totalCount ?? 0) === 0}
              onClick={() => void handleExport()}
              size="sm"
              variant="outline"
            >
              {exportProgress ? <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" /> : <Download aria-hidden className="mr-2 h-4 w-4" />}
              {exportProgress && exportProgress.total > 0
                ? t("mapaRxExporting", { done: exportProgress.done, total: exportProgress.total })
                : t("mapaRxExport")}
            </Button>
          </div>
          <DataTable
            columns={columns}
            data={data?.rows ?? []}
            empty={t("mapaRxEmptyTitle")}
            getRowId={(row) => row.id}
            onPageChange={setPage}
            onRowClick={handleRowClick}
            page={page}
            pageSize={PAGE_SIZE}
            totalItems={data?.totalCount ?? 0}
          />
        </div>
      )}

      {selectedPrescription ? (
        <PrescriptionViewDialog
          key={selectedPrescription.id}
          onOpenChange={(open) => {
            setIsViewOpen(open);
            if (!open) setSelectedPrescription(null);
          }}
          open={isViewOpen}
          prescription={selectedPrescription}
        />
      ) : null}
    </div>
  );
}
